import { ref, reactive, watch, Ref, computed } from 'vue'
import type { UploadInst, UploadFileInfo } from 'naive-ui'
import VueCropper from 'vue-cropperjs'
import { fileToDataUrl } from '@teams/common/utils'
import { uploadGroupBackground } from '@teams/common/api'
import { resolveRef, MaybeComputedRef } from '@vueuse/core'

export const useBackgroundEditor = (
  groupId: number,
  options: {
    url: MaybeComputedRef<string>
    onChange: (...args: unknown[]) => unknown
    allowEdit?: Ref<boolean>
    showBackgroundMaskBlur: Ref<boolean>
    usePcBackgroundToH5: Ref<boolean>
  }
) => {
  const imgUrl = resolveRef(options.url)
  const imgUrlCss = computed(() =>
    imgUrl.value ? `url(${imgUrl.value})` : `var(--theme-team-05)`
  )
  const props = ref({
    groupId,
    imgUrl,
    onBackgroundChange: options.onChange,
    allowEditBackground: options.allowEdit ?? ref(true),
    showBackgroundMaskBlur: options.showBackgroundMaskBlur,
    usePcBackgroundToH5: options.usePcBackgroundToH5
  })
  return reactive({
    imgUrl,
    imgUrlCss,
    props
  })
}

const showEditor = ref(false)
type cropperInst = typeof VueCropper['methods'] &
  typeof VueCropper['props'] & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cropper: any
  }
const uploadRef = ref<null | UploadInst>(null)
const cropperRef = ref<null | cropperInst>(null)
const cropperSrc = ref('')
export const useEditor = (options: {
  afterCrop?: (arg0: string) => void
  groupId: number
}) => {
  const showSelector = ref(false)
  const { afterCrop, groupId } = options
  if (!groupId) throw Error('no groupId with backgroundEditor')
  const updateShowSelector = (val = true) => {
    showSelector.value = val
  }
  const updateShowOrCloseSelector = () => {
    showSelector.value = !showSelector.value
  }

  const updateShowEditor = (val = true) => {
    showEditor.value = val
  }

  const handleChange = async (options: { file: UploadFileInfo }) => {
    const { file } = options
    updateShowSelector(false)
    if (file?.file) {
      const value = (await fileToDataUrl(file.file)) as string
      cropperSrc.value = value
      updateShowEditor(true)
    }
  }
  const onCrop = async () => {
    const imgData = cropperRef.value?.cropper
      .getCroppedCanvas()
      .toDataURL() as string
    const imgUrl = await uploadBackground(imgData)
    if (imgUrl) {
      await afterCrop?.(imgUrl)
      updateShowEditor(false)
    }
  }

  const onCancel = () => {
    updateShowEditor(false)
  }
  // 关闭弹窗
  const closeBackgroundEditor = () => {
    updateShowSelector(false)
  }
  const uploadBackground = async (fileValue: string) => {
    let picUrl: string | undefined
    try {
      const res = await uploadGroupBackground({
        data: fileValue,
        group_id: groupId
      })
      picUrl = res.data.pic
    } catch (e) {
      //
      console.log(e)
    }
    return picUrl
  }

  const percentValue = ref(0)
  const cropperZoom = (percent: number) => {
    if (percentValue.value === 0 && percent < 0) return
    if (percentValue.value === 100 && percent > 0) return
    percentValue.value = percentValue.value + percent * 100
  }

  watch(percentValue, (newVal, oldVal) => {
    if (newVal > oldVal) {
      cropperRef.value?.cropper.zoom?.(0.1)
    } else {
      cropperRef.value?.cropper.zoom?.(-0.1)
    }
  })

  return reactive({
    showEditor,
    updateShowEditor,
    uploadRef,
    handleChange,
    showSelector,
    updateShowSelector,
    updateShowOrCloseSelector,
    cropperRef,
    cropperSrc,
    onCrop,
    percentValue,
    cropperZoom,
    onCancel,
    closeBackgroundEditor
  })
}
